import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApply } from "@/context/ApplyContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { telHref, waHref } from "@/lib/links";

const EMPTY = {
  fullName: "", phone: "", whatsapp: "", email: "", dob: "", gender: "",
  stateOfOrigin: "", programme: "", track: "", intendedCourse: "",
  preferredUniversity: "", olevelStatus: "", utmeStatus: "", message: "",
};

const field = "w-full rounded-sm border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#0A1E4E] focus:ring-2 focus:ring-[#0A1E4E]/15";
const lbl = "text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5 block";

export function ApplyModal() {
  const apply = useApply();
  const { config } = useSiteConfig();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    apply.closeApply();
    setTimeout(() => { setDone(false); setForm(EMPTY); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.phone.trim()) {
      toast.error("Please provide your full name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Enquiry submitted successfully.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {apply?.open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-[#040c24]/70 backdrop-blur-sm p-0 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          data-testid="apply-modal-overlay"
        >
          <motion.div
            className="relative w-full max-w-2xl bg-[#F7F5EF] shadow-2xl sm:rounded-sm my-0 sm:my-8"
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="apply-modal"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between bg-[#0A1E4E] px-6 py-4 text-white">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Enquiry / Application</div>
                <h3 className="font-serif text-lg font-bold">School of Basic Studies</h3>
              </div>
              <button onClick={close} className="p-1 text-white/70 hover:text-white" data-testid="apply-modal-close">
                <X size={22} />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="apply-success">
                <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
                <h3 className="mt-4 font-serif text-2xl font-bold text-[#0A1E4E]">Enquiry Received</h3>
                <p className="mt-2 text-slate-600">
                  Thank you, {form.fullName || "candidate"}. An admissions officer will reach out to you shortly.
                  You can also contact us directly now.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                  <a href={waHref(config.whatsapp)} target="_blank" rel="noopener noreferrer" className="btn-red" data-testid="apply-success-whatsapp">
                    <MessageCircle size={16} /> Chat on WhatsApp
                  </a>
                  <a href={telHref(config.phonePrimary)} className="btn-navy" data-testid="apply-success-call">
                    <Phone size={16} /> Call {config.phonePrimary}
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 space-y-4" data-testid="apply-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Full Name *</label>
                    <input className={field} value={form.fullName} onChange={set("fullName")} data-testid="apply-fullName" required />
                  </div>
                  <div>
                    <label className={lbl}>Phone Number *</label>
                    <input className={field} value={form.phone} onChange={set("phone")} data-testid="apply-phone" required />
                  </div>
                  <div>
                    <label className={lbl}>WhatsApp Number</label>
                    <input className={field} value={form.whatsapp} onChange={set("whatsapp")} data-testid="apply-whatsapp" />
                  </div>
                  <div>
                    <label className={lbl}>Email</label>
                    <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="apply-email" />
                  </div>
                  <div>
                    <label className={lbl}>Date of Birth</label>
                    <input type="date" className={field} value={form.dob} onChange={set("dob")} data-testid="apply-dob" />
                  </div>
                  <div>
                    <label className={lbl}>Gender</label>
                    <select className={field} value={form.gender} onChange={set("gender")} data-testid="apply-gender">
                      <option value="">Select</option><option>Male</option><option>Female</option>
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>State of Origin</label>
                    <input className={field} value={form.stateOfOrigin} onChange={set("stateOfOrigin")} data-testid="apply-state" />
                  </div>
                  <div>
                    <label className={lbl}>Programme</label>
                    <select className={field} value={form.track} onChange={set("track")} data-testid="apply-track">
                      <option value="">Select JUPEB / IJMB / Remedial</option><option>JUPEB</option><option>IJMB</option><option>Remedial</option>
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Intended Course</label>
                    <input className={field} value={form.intendedCourse} onChange={set("intendedCourse")} data-testid="apply-course" />
                  </div>
                  <div>
                    <label className={lbl}>Preferred University</label>
                    <input className={field} value={form.preferredUniversity} onChange={set("preferredUniversity")} data-testid="apply-university" />
                  </div>
                  <div>
                    <label className={lbl}>O'Level Status</label>
                    <select className={field} value={form.olevelStatus} onChange={set("olevelStatus")} data-testid="apply-olevel">
                      <option value="">Select</option><option>Completed</option><option>Awaiting Result</option><option>SSS3 (In Progress)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={lbl}>Message</label>
                  <textarea rows={3} className={field} value={form.message} onChange={set("message")} data-testid="apply-message" />
                </div>

                <button type="submit" className="btn-red w-full !py-4" disabled={loading} data-testid="application-form-submit-button">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Submit Enquiry"}
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <a href={waHref(config.whatsapp)} target="_blank" rel="noopener noreferrer" className="btn-navy !py-3 !text-xs" data-testid="apply-form-whatsapp">
                    <MessageCircle size={15} /> Chat on WhatsApp
                  </a>
                  <a href={telHref(config.phonePrimary)} className="btn-gold !py-3 !text-xs" data-testid="apply-form-call">
                    <Phone size={15} /> Call {config.phonePrimary}
                  </a>
                </div>
                <p className="text-center text-[11px] text-slate-500">
                  We only collect the information needed to process your enquiry.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
